import { MouseEvent, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, ImagePlus, Loader2 } from "lucide-react";
import { InlineEditableInput } from "@/components/ui/InlineEditableInput";
import { useMediaQuery } from "@/hooks/useMediaQuery";

import { Asset } from "../types";
import { formatBytes } from "../utils";
import { groupAssetsByVariant } from "../assetVariants";
import { IMAGE_PRESETS } from "../imagePresets";
import { AssetPreview } from "./AssetPreview";

type AssetListProps = {
  assets: Asset[];
  selectedAssetIds: string[];
  onAssetClick: (asset: Asset) => void;
  onAssetDoubleClick: (asset: Asset) => void;
  onAssetContextMenu: (id: string, event: MouseEvent) => void;
  onToggleAssetSelection: (id: string, checked: boolean) => void;
  onRenameAsset: (id: string, name: string) => void;
  onCreatePresetVariant?: (
    asset: Asset,
    presetId: string,
  ) => Promise<Asset | null>;
  renameAssetId: string | null;
  showCheckbox?: boolean;
  enableContextMenu?: boolean;
};

export function AssetList({
  assets,
  selectedAssetIds,
  onAssetClick,
  onAssetDoubleClick,
  onAssetContextMenu,
  onToggleAssetSelection,
  onRenameAsset,
  onCreatePresetVariant,
  renameAssetId,
  showCheckbox = true,
  enableContextMenu = true,
}: AssetListProps) {
  const groups = useMemo(() => groupAssetsByVariant(assets), [assets]);
  const [expandedAssetIds, setExpandedAssetIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [creatingPreset, setCreatingPreset] = useState<{
    assetId: string;
    presetId: string;
  } | null>(null);
  const [presetMenuAssetId, setPresetMenuAssetId] = useState<string | null>(
    null,
  );
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const toggleExpanded = (assetId: string) => {
    setExpandedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const expandAsset = (assetId: string) => {
    setExpandedAssetIds((prev) => new Set(prev).add(assetId));
  };

  const createPreset = async (asset: Asset, presetId: string) => {
    if (!onCreatePresetVariant || creatingPreset) return;
    setCreatingPreset({ assetId: asset.id, presetId });
    try {
      const created = await onCreatePresetVariant(asset, presetId);
      if (created) {
        expandAsset(asset.id);
        setPresetMenuAssetId(null);
        onAssetClick(created);
      }
    } finally {
      setCreatingPreset(null);
    }
  };

  const renderAssetRow = (
    asset: Asset,
    options?: { variants?: Asset[]; isVariant?: boolean },
  ) => {
    const variants = options?.variants ?? [];
    const isVariant = options?.isVariant ?? false;
    const isSelected = selectedAssetIds.includes(asset.id);
    const isRenaming = renameAssetId === asset.id;
    const hasVariants = variants.length > 0;
    const expanded = expandedAssetIds.has(asset.id);
    const canCreatePreset =
      !isVariant &&
      asset.mediaType === "image" &&
      Boolean(onCreatePresetVariant);
    const menuOpen = presetMenuAssetId === asset.id;

    return (
      <div
        key={asset.id}
        className={`grid cursor-pointer grid-cols-3 items-center px-4 py-2 text-sm ${
          isSelected ? "bg-pinkCustom/15" : "hover:bg-pinkCustom/5"
        } ${isVariant ? "pl-10 text-gray-700" : ""}`}
        onClick={() => onAssetClick(asset)}
        onDoubleClick={() => onAssetDoubleClick(asset)}
        onContextMenu={(e) => {
          if (!enableContextMenu) return;
          e.preventDefault();
          onAssetContextMenu(asset.id, e);
        }}
      >
        <div className="col-span-2 flex min-w-0 items-center gap-2">
          {showCheckbox ? (
            <input
              type="checkbox"
              className="accent-pinkCustom h-4 w-4"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleAssetSelection(asset.id, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : null}
          {hasVariants ? (
            <button
              type="button"
              className="rounded p-0.5 text-gray-500 hover:bg-gray-100"
              aria-label={expanded ? "Hide variants" : "Show variants"}
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(asset.id);
              }}
            >
              <ChevronRight
                size={15}
                className={`transition-transform ${expanded ? "rotate-90" : ""}`}
              />
            </button>
          ) : (
            <span className="w-4" aria-hidden="true" />
          )}
          <AssetPreview asset={asset} size={isVariant ? 24 : 28} />
          <div className="min-w-0 flex-1 truncate">
            <InlineEditableInput
              value={asset.originalFilename}
              onSave={(next) => onRenameAsset(asset.id, next)}
              className="truncate"
              startEditing={isRenaming}
              enableClickToEdit={false}
            />
            {isVariant ? (
              <div className="text-[10px] tracking-wide text-gray-400 uppercase">
                Variant
              </div>
            ) : null}
          </div>
          {canCreatePreset && isDesktop ? (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {IMAGE_PRESETS.map((preset) => {
                const isCreating =
                  creatingPreset?.assetId === asset.id &&
                  creatingPreset.presetId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    title={`Create ${preset.label} variant`}
                    aria-label={`Create ${preset.label} variant`}
                    disabled={Boolean(creatingPreset)}
                    onClick={(event) => {
                      event.stopPropagation();
                      void createPreset(asset, preset.id);
                    }}
                    className="hover:border-pinkCustom hover:bg-pinkCustom/5 flex h-7 items-center gap-1 rounded border border-gray-200 px-2 text-[10px] font-medium text-gray-600 transition disabled:cursor-wait disabled:opacity-60"
                  >
                    {isCreating ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : null}
                    {preset.label}
                  </button>
                );
              })}
            </div>
          ) : null}
          {canCreatePreset && !isDesktop ? (
            <div className="relative ml-auto shrink-0">
              <button
                type="button"
                aria-label="Create image variant"
                aria-expanded={menuOpen}
                disabled={Boolean(creatingPreset)}
                onClick={(event) => {
                  event.stopPropagation();
                  setPresetMenuAssetId(menuOpen ? null : asset.id);
                }}
                className="hover:bg-pinkCustom/10 hover:text-pinkCustom flex h-11 w-11 items-center justify-center rounded-full text-gray-600 transition disabled:cursor-wait disabled:opacity-60"
              >
                {creatingPreset?.assetId === asset.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ImagePlus className="h-4 w-4" />
                )}
              </button>
              {menuOpen ? (
                <div
                  className="absolute top-full right-0 z-20 mt-1 w-52 rounded-lg border flex justify-between items-center border-gray-200 bg-white py-3 px-1.5 shadow-lg divide-x  divide-pinkCustom"
                  onClick={(event) => event.stopPropagation()}
                >
                  {IMAGE_PRESETS.map((preset) => {
                    const isCreating =
                      creatingPreset?.assetId === asset.id &&
                      creatingPreset.presetId === preset.id;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        disabled={Boolean(creatingPreset)}
                        onClick={() => void createPreset(asset, preset.id)}
                        className="hover:bg-pinkCustom/5 flex min-h-6 items-center justify-between px-2 text-left text-xs text-gray-700 transition disabled:cursor-wait disabled:opacity-60"
                      >
                        <span>{preset.label}</span>
                        {isCreating ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="text-right text-xs text-gray-600">
          {formatBytes(asset.size)}
        </div>
      </div>
    );
  };

  return (
    <>
      {groups.map(({ asset, variants }) => {
        const expanded = expandedAssetIds.has(asset.id);
        return (
          <div key={asset.id}>
            {renderAssetRow(asset, { variants })}
            <AnimatePresence initial={false}>
              {expanded && variants.length ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="border-pinkCustom/20 overflow-hidden border-l-2 bg-gray-50/60"
                >
                  {variants.map((variant) =>
                    renderAssetRow(variant, { isVariant: true }),
                  )}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        );
      })}
    </>
  );
}
